import dayjs, { Dayjs } from "dayjs";
import { FieldAlias } from "../lib/enums";
import { CalendarEvent } from "../models/Calendar";
import { FormGetResponse } from "../models/Form";
import { Treatment } from "../models/Treatments";
import { Measurement } from "../models/Measurements";
import { translateNoteFieldAlias } from "./fieldHelpers";

// Forms

export const convertEQ5D5LFormsToCalendarEvents = (
  forms: FormGetResponse[]
) => {
  const eQ5D5LCalendarEvents = [] as CalendarEvent[];
  forms.forEach((form) => {
    const values = form.values;
    if (values.length === 0) {
      return;
    }
    const score = values.find(
      (v) => v.fieldAlias === FieldAlias.D_EQ5D_6_VAS
    )?.value;
    const date = values.find(
      (v) => v.fieldAlias === FieldAlias.D_EQ5D_DATUM
    )?.value;
    const eq5 = {
      title: `Hälsotillstånd: ${score}`,
      start: date,
      allDay: true,
      color: "#00bcd4",
      textColor: "black",
    } as CalendarEvent;
    eQ5D5LCalendarEvents.push(eq5);
  });
  return eQ5D5LCalendarEvents;
};

export const convertImmunoglobulinFormsToCalendarEvents = (
  forms: FormGetResponse[],
  mappedSideEffects: Map<number, string>
) => {
  const immunoglobulinCalendarEvents = [] as CalendarEvent[];
  forms.forEach((form) => {
    const values = form.values;
    if (values.length === 0) {
      return;
    }
    const sideEffects = values.find(
      (v) => v.fieldAlias === FieldAlias.D_PERGAMMA_5_ADVERSE_EVENT
    )?.value;
    const mSideEffects = sideEffects
      ? sideEffects
          .split(",")
          .map((a: any) => mappedSideEffects?.get(parseInt(a, 10)) || a)
          .join(", ")
      : "";
    const date = values.find(
      (v) => v.fieldAlias === FieldAlias.D_PERGAMMA_1_DATE
    )?.value;
    const ig = {
      title: `Biverkningar: ${mSideEffects}`,
      start: date,
      allDay: true,
      color: "#4cb050",
      textColor: "black",
    } as CalendarEvent;
    immunoglobulinCalendarEvents.push(ig);
  });
  return immunoglobulinCalendarEvents;
};

// Sick Days

export const convertSickDayFormsToCalendarEvents = (
  forms: FormGetResponse[]
) => {
  const sickDayCalendarEvents = [] as CalendarEvent[];
  forms.forEach((form) => {
    const values = form.values;
    if (values.length === 0) {
      return;
    }
    const startDate = values.find(
      (v) => v.fieldAlias === FieldAlias.D_SJUKSKRIVNING_1_START_DATUM
    )?.value;

    const endDate = values.find(
      (v) => v.fieldAlias === FieldAlias.D_SJUKSKRIVNING_2_SLUT_DATUM
    )?.value;

    const sickDay = {
      title: `Sjukdagar`,
      start: dayjs(startDate).toDate(),
      end: endDate ? dayjs(endDate).toDate() : undefined,
      allDay: true,
      color: "#f44236",
      textColor: "black",
    } as CalendarEvent;
    sickDayCalendarEvents.push(sickDay);
  });
  return sickDayCalendarEvents;
};

// CareDays

export const convertCareDayFormsToCalendarEvents = (
  forms: FormGetResponse[]
) => {
  const careDayCalendarEvents = [] as CalendarEvent[];
  forms.forEach((form) => {
    const values = form.values;
    if (values.length === 0) {
      return;
    }
    const startDate = values.find(
      (v) => v.fieldAlias === FieldAlias.D_CAREDAYS_1_DATESTART
    )?.value;

    const endDate = values.find(
      (v) => v.fieldAlias === FieldAlias.D_CAREDAYS_2_DATEEND
    )?.value;

    const careDay = {
      title: `Vårddagar`,
      start: dayjs(startDate).toDate(),
      end: endDate ? dayjs(endDate).toDate() : undefined,
      allDay: true,
      color: "#ad1457",
      textColor: "black",
    } as CalendarEvent;
    careDayCalendarEvents.push(careDay);
  });
  return careDayCalendarEvents;
};

// Symptoms

export const convertSymptomsToCalendarEvents = (
  daySymptoms: Map<string, string[]>,
  mappedSymptomNames: Map<string, string>
): CalendarEvent[] => {
  const symptomCalendarEvents = [] as CalendarEvent[];
  const days = Array.from(daySymptoms.keys());
  const getDailySymptoms = (date: string) => {
    const symptoms = daySymptoms?.get(date);
    if (!symptoms) {
      return "";
    }
    return symptoms.map((s) => mappedSymptomNames?.get(s)).join(", ");
  };
  days.forEach((item) => {
    symptomCalendarEvents.push({
      title: getDailySymptoms(item),
      start: dayjs(item).toDate(),
      allDay: true,
      color: "#f44236",
      textColor: "black",
    });
  });
  return symptomCalendarEvents;
};

// Antibiotics / Treatments

export const convertAntibioticsToCalendarEvents = (
  treatments: Treatment[],
  mappedDrugs: Map<number, string>
): CalendarEvent[] => {
  const treatmentCalendarEvents = [] as CalendarEvent[];
  treatments.forEach((item) => {
    const { drugId, startDate, endDate } = item;
    const drug = mappedDrugs?.get(drugId);
    treatmentCalendarEvents.push({
      title: drug || drugId.toString(),
      start: dayjs(startDate).toDate(),
      end: endDate ? dayjs(endDate).toDate() : undefined,
      allDay: true,
      color: "#ff9700",
      textColor: "black",
    });
  });
  return treatmentCalendarEvents;
};

// Vaccinations / Measurements

export const convertVaccinesToCalendarEvents = (
  vaccinations: Measurement[],
  mappedVaccines: Map<number, string>
): CalendarEvent[] => {
  const treatmentCalendarEvents = [] as CalendarEvent[];
  vaccinations.forEach((item) => {
    const { date, value } = item;
    const vaccine = mappedVaccines?.get(parseInt(value, 10));
    treatmentCalendarEvents.push({
      title: vaccine || value.toString(),
      start: dayjs(date).toDate(),
      allDay: true,
      color: "#ffeb3c",
      textColor: "black",
    });
  });
  return treatmentCalendarEvents;
};

export const convertMyNotesToCalendarEvents = (
  myNotes: Measurement[]
): CalendarEvent[] => {
  const treatmentCalendarEvents = [] as CalendarEvent[];
  myNotes.forEach((item) => {
    const { date, value, fieldAlias } = item;
    const noteType = translateNoteFieldAlias(fieldAlias);
    treatmentCalendarEvents.push({
      title: `${noteType} - ${value}`,
      start: dayjs(date).toDate(),
      allDay: true,
      color: "#00bcd5",
      textColor: "black",
    });
  });
  return treatmentCalendarEvents;
};
